"use client";

import { CircleAlertIcon, CircleCheckIcon, InfoIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";
import { HOVER_EASE } from "./states";

// Design spec §8.2 Notice (DTP `.notice`): padding 14 16, radius 8, 13 px / 1.6, a 1 px border
// that is the soft fill mixed 12 % towards the tone's text. Fills and text are the status
// soft/text pairs (all >= 4.5:1); neutral is --secondary with --text-secondary. App banners and
// load errors use this; `Alert` stays for inline form messages.

export type NoticeTone = "neutral" | "info" | "success" | "warning" | "danger";

const TONE: Record<NoticeTone, string> = {
  neutral:
    "bg-secondary text-secondary border-[color-mix(in_srgb,var(--text-secondary)_12%,var(--secondary))]",
  info: "bg-info-soft text-info-text border-[color-mix(in_srgb,var(--info-text)_12%,var(--info-soft))]",
  success:
    "bg-success-soft text-success-text border-[color-mix(in_srgb,var(--success-text)_12%,var(--success-soft))]",
  warning:
    "bg-warning-soft text-warning-text border-[color-mix(in_srgb,var(--warning-text)_12%,var(--warning-soft))]",
  danger:
    "bg-danger-soft text-danger-text border-[color-mix(in_srgb,var(--danger-text)_12%,var(--danger-soft))]",
};

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

const ICON: Record<NoticeTone, IconType> = {
  neutral: InfoIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
};

export type NoticeProps = Omit<React.ComponentProps<"div">, "title"> & {
  tone?: NoticeTone;
  /** Leading 18 px icon; the tone's icon by default, `null` for none. */
  icon?: IconType | null;
  title?: React.ReactNode;
  /** A button or link on the right. */
  action?: React.ReactNode;
  /** Shows a close button; `onDismiss` runs after the notice hides itself. */
  dismissible?: boolean;
  onDismiss?: () => void;
};

export function Notice({
  tone = "neutral",
  icon,
  title,
  action,
  dismissible = false,
  onDismiss,
  className,
  children,
  ...props
}: NoticeProps) {
  const [hidden, setHidden] = React.useState(false);
  if (hidden) return null;
  const Icon = icon === null ? null : (icon ?? ICON[tone]);
  return (
    <div
      data-slot="notice"
      data-tone={tone}
      className={cn(
        // One-line notices centre the icon, text and action; titled ones top-align them.
        "flex gap-3 rounded-md border px-4 py-3.5 text-sm leading-[1.6]",
        title ? "items-start" : "items-center",
        TONE[tone],
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon className={cn("size-[18px] shrink-0", title && "mt-px")} aria-hidden={true} />
      ) : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center self-center">{action}</div> : null}
      {dismissible ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            setHidden(true);
            onDismiss?.();
          }}
          className={cn(
            "-my-0.5 -mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-sm opacity-80 hover:opacity-100 hover:bg-[color-mix(in_srgb,currentColor_10%,transparent)]",
            HOVER_EASE,
            FOCUS_RING,
          )}
        >
          <XIcon className="size-4" aria-hidden={true} />
        </button>
      ) : null}
    </div>
  );
}
